import { Button, Card, Cascader, Form, Input, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { shippingRequestHttp } from "@/api";
import { areaLib } from "@/utils/areaLib";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import {
    AddressAutocomplete,
    getSuggestionAreaCodes,
    getSuggestionGeo,
    mapAreaCascaderOptions,
    type AreaCascaderOption
} from "@/views/components/AddressAutocomplete";
import { PORT_WHARF_BERTH_TYPE } from "@zhihaoscm/meta";
import { Dialog as ZhDialog, Grid as ZhGrid, GridItem as ZhGridItem } from "zhihao-ui-react";

export interface PortManagementFormValues {
    type?: number;
    name: string;
    shortName: string;
    harborCode?: string | null;
    area?: string | null;
    contactName?: string | null;
    phone?: string | null;
    provinceCode: string;
    cityCode: string;
    regionCode: string;
    areaCode?: string[];
    address: string;
    fullAddress?: string;
    latLon: string;
    remark?: string | null;
}

export interface PortManagementFormPageProps {
    portId?: number | string;
    loadPort?: (id: number) => Promise<PortWharfBerthVo>;
    createPort?: (payload: PortManagementFormValues) => Promise<unknown>;
    updatePort?: (id: number, payload: PortManagementFormValues) => Promise<unknown>;
}

/** 复用 Vue 港口详情接口。 */
export async function loadPortManagementDetail(id: number): Promise<PortWharfBerthVo> {
    const response = await shippingRequestHttp.service.get<PortWharfBerthVo>(`/port/${id}`);
    return response.data;
}

/** 复用 Vue 港口新增接口。 */
export async function createPortManagement(payload: PortManagementFormValues) {
    const response = await shippingRequestHttp.service.post("/port", payload);
    return response.data;
}

/** 复用 Vue 港口编辑接口。 */
export async function updatePortManagement(id: number, payload: PortManagementFormValues) {
    const response = await shippingRequestHttp.service.put(`/port/${id}`, payload);
    return response.data;
}

const phonePattern = /^((1[3-9]\d{9})|((0\d{2,3}-?)?\d{7,8}(-\d{3,4})?))$/;
const chineseNamePattern = /^[\u4e00-\u9fa5]+$/;

/** Vue 页面将空字符串转换成 null 后再提交，避免编辑时残留空值语义不一致。 */
const emptyStringToNull = (value: string | null | undefined) => (value === "" || value === undefined ? null : value);

/** 物流管理-港口新增/编辑，覆盖 Vue 核心字段、回显、校验和 POST/PUT payload。 */
export function PortManagementFormPage({
    portId,
    loadPort = loadPortManagementDetail,
    createPort = createPortManagement,
    updatePort = updatePortManagement
}: PortManagementFormPageProps) {
    const navigate = useNavigate();
    const params = useParams<{ id?: string }>();
    const [searchParams] = useSearchParams();
    const currentId = portId ?? params.id ?? searchParams.get("id") ?? undefined;
    const [form] = Form.useForm<PortManagementFormValues>();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [pendingPayload, setPendingPayload] = useState<PortManagementFormValues>();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [areaOptions, setAreaOptions] = useState<AreaCascaderOption[]>([]);
    const regionCode = Form.useWatch("regionCode", form);

    useEffect(() => {
        // 地区库加载完成后统一生成三级级联选项。
        void areaLib.ready.then(() => setAreaOptions(mapAreaCascaderOptions(areaLib.getAreaList())));
    }, []);

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadPort(Number(currentId))
            .then(detail => {
                if (!disposed)
                    form.setFieldsValue({
                        ...detail,
                        areaCode: [detail.provinceCode, detail.cityCode, detail.regionCode].filter(Boolean)
                    } as unknown as PortManagementFormValues);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("港口详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, form, loadPort]);

    // 复用 Vue 提交前的确认弹窗，并将港口类型固定为 HARBOR=2。
    const submitForm = (values: PortManagementFormValues) => {
        // Vue 提交时按省市区编码重新生成完整地址；地区库暂未加载时回退到编辑回显值。
        const areaName = areaLib.getFullAreaName(values.provinceCode, values.cityCode, values.regionCode);
        const address = values.address.trim();
        const payload: PortManagementFormValues = {
            type: PORT_WHARF_BERTH_TYPE.HARBOR.code,
            name: values.name.trim(),
            shortName: values.shortName.trim(),
            harborCode: emptyStringToNull(values.harborCode) as string | null,
            area: emptyStringToNull(values.area) as string | null,
            contactName: emptyStringToNull(values.contactName) as string | null,
            phone: emptyStringToNull(values.phone) as string | null,
            provinceCode: values.provinceCode,
            cityCode: values.cityCode,
            regionCode: values.regionCode,
            address,
            fullAddress: areaName ? `${areaName}${address}` : values.fullAddress || address,
            latLon: values.latLon,
            remark: emptyStringToNull(values.remark) as string | null
        };
        setPendingPayload(payload);
        setConfirmOpen(true);
    };

    // 确认后调用 Vue 对应的新增或编辑接口，并保留失败反馈。
    const confirmSubmit = async () => {
        if (!pendingPayload) return;
        setLoading(true);
        setErrorMessage("");
        try {
            if (currentId) await updatePort(Number(currentId), pendingPayload);
            else await createPort(pendingPayload);
            notifyOperationSuccess("提交成功");
            setConfirmOpen(false);
            navigate(-1);
        } catch {
            setErrorMessage(currentId ? "港口修改失败" : "港口创建失败");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section aria-label="port-management-form-page" className="flex min-h-full flex-col">
            <Card>
                <Typography.Title level={4}>{currentId ? "修改港口" : "新增港口"}</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form<PortManagementFormValues>
                    form={form}
                    layout="vertical"
                    initialValues={{ type: PORT_WHARF_BERTH_TYPE.HARBOR.code }}
                    onFinish={values => void submitForm(values)}
                >
                    <Form.Item name="type" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="fullAddress" hidden>
                        <Input />
                    </Form.Item>
                    <ZhGrid cols={4} gap={[20, 0]}>
                        <ZhGridItem span={1}>
                            <Form.Item label="名称" name="name" rules={[{ required: true, message: "请输入港口名称" }]}>
                                <Input aria-label="名称" maxLength={40} placeholder="请输入港口名称" />
                            </Form.Item>
                        </ZhGridItem>
                        <Form.Item
                            label="简称"
                            name="shortName"
                            rules={[{ required: true, message: "请输入港口简称" }]}
                        >
                            <Input aria-label="简称" maxLength={20} placeholder="请输入港口简称" />
                        </Form.Item>
                        <Form.Item label="港口代码" name="harborCode">
                            <Input aria-label="港口代码" maxLength={10} placeholder="请输入港口代码" />
                        </Form.Item>
                        <Form.Item
                            label="面积"
                            name="area"
                            rules={[{ pattern: /^$|^\+?[1-9]\d*$/, message: "请输入正整数" }]}
                        >
                            <Input aria-label="面积" placeholder="请输入港口面积" suffix="㎡" />
                        </Form.Item>
                        <Form.Item
                            label="联系人"
                            name="contactName"
                            rules={[{ pattern: chineseNamePattern, message: "请输入中文姓名" }]}
                        >
                            <Input aria-label="联系人" maxLength={10} placeholder="请输入联系人" />
                        </Form.Item>
                        <Form.Item
                            label="手机号"
                            name="phone"
                            rules={[{ pattern: phonePattern, message: "请输入正确的手机号或固话" }]}
                        >
                            <Input aria-label="手机号" placeholder="请输入电话或手机号" />
                        </Form.Item>
                    </ZhGrid>
                    <Typography.Title level={5}>位置</Typography.Title>
                    <ZhGrid cols={4} gap={[20, 0]}>
                        <Form.Item
                            label="所在区域"
                            name="areaCode"
                            rules={[{ required: true, message: "请选择省市区" }]}
                        >
                            <Cascader
                                aria-label="所在区域"
                                options={areaOptions}
                                placeholder="请选择省市区"
                                showSearch
                                onChange={value => {
                                    const [provinceCode = "", cityCode = "", nextRegionCode = ""] = value.map(String);
                                    // 更换行政区后清空旧地址和坐标，避免提交不匹配的位置数据。
                                    form.setFieldsValue({
                                        provinceCode,
                                        cityCode,
                                        regionCode: nextRegionCode,
                                        address: "",
                                        latLon: ""
                                    });
                                }}
                            />
                        </Form.Item>
                        <Form.Item name="provinceCode" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item name="cityCode" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item name="regionCode" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="详细地址"
                            name="address"
                            rules={[{ required: true, message: "请输入详细地址" }]}
                        >
                            <AddressAutocomplete
                                aria-label="详细地址"
                                regionCode={regionCode}
                                onSelectSuggestion={suggestion => {
                                    const areaCodes = getSuggestionAreaCodes(suggestion);
                                    const geo = getSuggestionGeo(suggestion);
                                    // 候选地址可补齐未选择的行政区，并回填后端要求的“经度,纬度”。
                                    if (!regionCode && areaCodes.length) {
                                        const [provinceCode = "", cityCode = "", nextRegionCode = ""] = areaCodes;
                                        form.setFieldsValue({
                                            areaCode: areaCodes,
                                            provinceCode,
                                            cityCode,
                                            regionCode: nextRegionCode
                                        });
                                    }
                                    if (geo) form.setFieldValue("latLon", `${geo.lng},${geo.lat}`);
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            label="经纬度"
                            name="latLon"
                            rules={[{ required: true, message: "请选择位置地址后自动带出经纬度" }]}
                        >
                            <Input aria-label="经纬度" placeholder="请选择经纬度" />
                        </Form.Item>
                    </ZhGrid>
                    <Form.Item label="简介" name="remark">
                        <Input.TextArea aria-label="简介" maxLength={200} placeholder="请输入港口简介" />
                    </Form.Item>
                    <Space className="operation-fixed-btn">
                        <Button htmlType="submit" loading={loading} type="primary">
                            提交
                        </Button>
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Form>
            </Card>
            <ZhDialog
                title="提示"
                open={confirmOpen}
                modalRender={node => <div role="dialog">{node}</div>}
                footer={
                    <Space>
                        <Button onClick={() => setConfirmOpen(false)}>取消</Button>
                        <Button loading={loading} type="primary" onClick={() => void confirmSubmit()}>
                            确认
                        </Button>
                    </Space>
                }
                onCancel={() => setConfirmOpen(false)}
            >
                是否保存
            </ZhDialog>
        </section>
    );
}
